import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { BRAND } from "../lib/site";
import { useAuth } from "../context/AuthContext";
import { SRNAcknowledgement } from "./SRNAcknowledgement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, value, onChange, type = "text", required, testid }) => (
  <label className="block group">
    <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">{label}{required && " *"}</span>
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] transition-colors"
    />
  </label>
);

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", company: "", interest: "", message: "" };

export const Contact = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [ack, setAck] = useState(null);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/inquiries`, { ...form, user_id: user ? user.id : null });
      setAck({ ...r.data, submitted: form });
      toast.success("Inquiry filed to the Vinci Master Ledger.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36 border-t border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <span className="overline text-[#B87333]">/ Get in Touch</span>
          <h2 className="font-serif text-[clamp(2.2rem,5vw,5rem)] leading-[1] tracking-[-0.02em] mt-6">
            Let's move <span className="italic text-[#B87333]">material.</span>
          </h2>
          <p className="font-body text-[#8A8A82] leading-relaxed mt-8 max-w-md">
            Request a quote or open a bulk-trade conversation. Our onboarding is fully automated, transparent, and fast.
          </p>
          <div className="mt-12 space-y-6">
            <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="block group">
              <span className="overline text-[#8A8A82]">Email</span>
              <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] group-hover:text-[#B87333] transition-colors">{BRAND.email}</div>
            </a>
            <div data-testid="contact-phone">
              <span className="overline text-[#8A8A82]">Phone</span>
              <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-1">
                <span className="text-[#8A8A82] font-body text-base mr-2">Phone:</span>
                <a href={`tel:${BRAND.phones[0].replace(/\s/g, "")}`} data-testid="contact-phone-1" className="hover:text-[#B87333] transition-colors">{BRAND.phones[0]}</a>
                <span className="text-[#8A8A82] mx-3">/</span>
                <a href={`tel:${BRAND.phones[1].replace(/\s/g, "")}`} data-testid="contact-phone-2" className="hover:text-[#B87333] transition-colors">{BRAND.phones[1]}</a>
              </div>
            </div>
            <div data-testid="contact-address">
              <span className="overline text-[#8A8A82]">Registered Office</span>
              <address className="not-italic mt-2 font-serif text-lg md:text-xl text-[#F5F5F0] leading-[1.5]">
                <div className="text-[#B87333]">{BRAND.address.company}</div>
                {BRAND.address.lines.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
                <div>{BRAND.address.city} · District: {BRAND.address.district}</div>
                <div>{BRAND.address.state} — <span className="text-[#8A8A82]">PIN {BRAND.address.pincode}</span></div>
              </address>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {ack ? (
            <SRNAcknowledgement
              srn={ack.srn}
              title="Inquiry Filed"
              subtitle="Our trade desk will read your note and revert. Every reply we send will carry this SRN so we stay on one thread."
              kind="Contact Inquiry"
              summary={[
                ["Name", `${ack.submitted.first_name} ${ack.submitted.last_name}`.trim()],
                ["Email", ack.submitted.email],
                ...(ack.submitted.phone ? [["Phone", ack.submitted.phone]] : []),
                ...(ack.submitted.company ? [["Company", ack.submitted.company]] : []),
                ...(ack.submitted.interest ? [["Interest", ack.submitted.interest]] : []),
                ["Filed on", new Date().toLocaleString()],
              ]}
              actions={
                <button
                  data-testid="contact-file-another"
                  onClick={() => { setAck(null); setForm(EMPTY); }}
                  className="inline-flex items-center rounded-full border border-white/25 px-6 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#F5F5F0] hover:border-[#C9A961] transition-colors"
                >
                  File another inquiry
                </button>
              }
            />
          ) : (
            <motion.form
              data-testid="contact-form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              <Field label="First name" name="first_name" value={form.first_name} onChange={onChange} required testid="input-first-name" />
              <Field label="Last name" name="last_name" value={form.last_name} onChange={onChange} required testid="input-last-name" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="input-email" />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="input-phone" />
              <Field label="Company" name="company" value={form.company} onChange={onChange} testid="input-company" />
              <Field label="Material of interest" name="interest" value={form.interest} onChange={onChange} testid="input-interest" />
              <div className="md:col-span-2">
                <label className="block group">
                  <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">Message *</span>
                  <textarea
                    data-testid="input-message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    rows={5}
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] resize-none transition-colors"
                  />
                </label>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  type="submit"
                  data-testid="submit-inquiry-btn"
                  disabled={loading}
                  className="inline-flex items-center rounded-full bg-[#F5F5F0] text-black px-8 py-3.5 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] hover:text-[#F5F5F0] transition-colors disabled:opacity-50"
                >
                  {loading ? "Filing…" : "Send inquiry"}
                </button>
              </div>
            </motion.form>
          )}
        </div>
      </div>
    </section>
  );
};
